import { FC, useState } from 'react';

import { useTranslate } from 'hooks/useTranslate';
import {
  IncentiveOffer,
  IncentiveOfferOperation,
  IncentiveOfferPlanClass,
  IncentivesMarketBeneficiary,
} from 'modules/incentivesMarket/types';

import { getOfferStatus } from 'modules/incentivesMarket/utils/getOfferStatus';
import IncentivesMarketAdminPanelOperationsTable from '../../../components/IncentivesMarketAdminPanelOperationsTable';
import IncentivesMarketAdminPanelConfiguration from './IncentivesMarketAdminPanelConfiguration';
import IncentivesMarketAdminPanelOfferItem from './IncentivesMarketAdminPanelOfferItem';

/* eslint-disable no-unused-vars -- callback param names in type definitions */
type IncentivesMarketAdminPanelOffersListProps = {
  societyId: string;
  sellOffers: IncentiveOffer[];
  buyOffers: IncentiveOffer[];
  operations: IncentiveOfferOperation[];
  onViewOfferDetail: (offerId: string) => void;
  onViewOperationDetail: (operationId: string) => void;
  onUpdateOperationStatus: (
    operationId: string,
    status: string,
    comment?: string
  ) => void;
  onToggleBeneficiaryAccess: (beneficiaryIds: string[], enabled: boolean) => void;
  isLoading?: boolean;
};
/* eslint-enable no-unused-vars */

type TabKey = 'SELL' | 'BUY' | 'OPERATIONS' | 'CONFIG';

const getBeneficiaryName = (beneficiary: string | Partial<IncentivesMarketBeneficiary>) => {
  if (typeof beneficiary === 'string') return '';
  return beneficiary?.name || '';
};

const getShareClassName = (
  unitClass: string | Partial<IncentiveOfferPlanClass> | null
) => {
  if (!unitClass || typeof unitClass === 'string') return '-';
  return unitClass?.name || '-';
};

const IncentivesMarketAdminPanelOffersList: FC<
  IncentivesMarketAdminPanelOffersListProps
> = ({
  societyId,
  sellOffers,
  buyOffers,
  operations,
  onViewOfferDetail,
  onViewOperationDetail,
  onUpdateOperationStatus,
  onToggleBeneficiaryAccess,
  isLoading = false,
}) => {
  const { t } = useTranslate();
  const [activeTab, setActiveTab] = useState<TabKey>('SELL');

  return (
    <>
      <ul
        className="nav nav-tabs nav-tabs-s1 nav-panel-admin mb-4 d-flex flex-wrap w-100 align-items-end"
        role="tablist"
      >
        <li className="nav-item" role="presentation">
          <button
            type="button"
            className={`nav-link bg-transparent ${
              activeTab === 'SELL' ? 'active' : ''
            }`}
            onClick={() => setActiveTab('SELL')}
            role="tab"
            aria-selected={activeTab === 'SELL'}
          >
            {t('SellOffers')}
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            type="button"
            className={`nav-link bg-transparent ${
              activeTab === 'BUY' ? 'active' : ''
            }`}
            onClick={() => setActiveTab('BUY')}
            role="tab"
            aria-selected={activeTab === 'BUY'}
          >
            {t('BuyOffers')}
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            type="button"
            className={`nav-link bg-transparent ${
              activeTab === 'OPERATIONS' ? 'active' : ''
            }`}
            onClick={() => setActiveTab('OPERATIONS')}
            role="tab"
            aria-selected={activeTab === 'OPERATIONS'}
          >
            {t('Operations')}
          </button>
        </li>
        <li className="nav-item ml-auto pr-0" role="presentation">
          <button
            type="button"
            className={`nav-link bg-transparent ${
              activeTab === 'CONFIG' ? 'active' : ''
            }`}
            onClick={() => setActiveTab('CONFIG')}
            role="tab"
            aria-selected={activeTab === 'CONFIG'}
          >
            <em className="icon ni ni-setting mr-1" />
            {t('IncentivesMarketAdminConfigurationTab')}
          </button>
        </li>
      </ul>

      <div className="tab-content">
        {/* Tab Ofertas de venta */}
        <div
          className={`tab-pane fade ${
            activeTab === 'SELL' ? 'show active' : ''
          }`}
          role="tabpanel"
        >
          <div className="card card-bordered mb-4">
            <div className="card-inner">
              <h6 className="card-title mb-3">
                <em className="icon ni ni-wallet-out text-primary mr-2" />
                {t('AllSellOffers')}
              </h6>
              <p className="text-soft fs-13px mb-4">
                {t('OffersTabDescriptionSell')}
              </p>
              {sellOffers.length === 0 ? (
                <div className="text-center py-4">
                  <em className="icon ni ni-inbox fs-3 text-soft d-block mb-2" />
                  <p className="text-soft">{t('NoOffersAvailable')}</p>
                </div>
              ) : (
                <div className="row g-3">
                  {sellOffers.map((offer) => (
                    <IncentivesMarketAdminPanelOfferItem
                      key={offer._id}
                      offer={offer}
                      isSell
                      onViewDetail={onViewOfferDetail}
                      getOfferStatus={getOfferStatus}
                      getBeneficiaryName={getBeneficiaryName}
                      getShareClassName={getShareClassName}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tab Ofertas de compra */}
        <div
          className={`tab-pane fade ${
            activeTab === 'BUY' ? 'show active' : ''
          }`}
          role="tabpanel"
        >
          <div className="card card-bordered mb-4">
            <div className="card-inner">
              <h6 className="card-title mb-3">
                <em className="icon ni ni-wallet-in text-primary mr-2" />
                {t('AllBuyOffers')}
              </h6>
              <p className="text-soft fs-13px mb-4">
                {t('OffersTabDescriptionBuy')}
              </p>
              {buyOffers.length === 0 ? (
                <div className="text-center py-4">
                  <em className="icon ni ni-inbox fs-3 text-soft d-block mb-2" />
                  <p className="text-soft">{t('NoOffersAvailable')}</p>
                </div>
              ) : (
                <div className="row g-3">
                  {buyOffers.map((offer) => (
                    <IncentivesMarketAdminPanelOfferItem
                      key={offer._id}
                      offer={offer}
                      isSell={false}
                      onViewDetail={onViewOfferDetail}
                      getOfferStatus={getOfferStatus}
                      getBeneficiaryName={getBeneficiaryName}
                      getShareClassName={getShareClassName}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tab Operaciones */}
        <div
          className={`tab-pane fade ${
            activeTab === 'OPERATIONS' ? 'show active' : ''
          }`}
          role="tabpanel"
        >
          <div className="card card-bordered mb-4">
            <div className="card-inner">
              <h6 className="card-title mb-3">
                <em className="icon ni ni-list text-primary mr-2" />
                {t('AllOperations')}
              </h6>
              <p className="text-soft fs-13px mb-4">
                {t('OperationsTabDescription')}
              </p>
              <IncentivesMarketAdminPanelOperationsTable
                operations={operations}
                offers={[...sellOffers, ...buyOffers]}
                onViewOperationDetail={onViewOperationDetail}
                onUpdateOperationStatus={onUpdateOperationStatus}
                embedded
              />
            </div>
          </div>
        </div>

        {/* Tab Configuración */}
        <div
          className={`tab-pane fade ${
            activeTab === 'CONFIG' ? 'show active' : ''
          }`}
          role="tabpanel"
        >
          <IncentivesMarketAdminPanelConfiguration
            societyId={societyId}
            onToggleBeneficiaryAccess={onToggleBeneficiaryAccess}
            isLoading={isLoading}
          />
        </div>
      </div>
    </>
  );
};

export default IncentivesMarketAdminPanelOffersList;
